<script setup>
import { Comment, computed, Fragment, Text, useSlots } from 'vue';

const props = defineProps({
  icon: { type: String, required: true },
  iconGradient: { type: String, default: 'bg-n-slate-4' },
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  subtitleIcon: { type: String, default: 'i-lucide-link-2' },
  statusLabel: { type: String, default: '' },
  statusClass: { type: String, default: 'bg-n-slate-3 text-n-slate-11' },
  badges: { type: Array, default: () => [] },
  // Pares label/valor. Um item com `highlight: true` sobe para a faixa do topo
  // do bloco (com ícone, valor grande e o status à direita); os demais viram
  // linhas `rótulo → valor` alinhadas.
  details: { type: Array, default: () => [] },
  metaLine: { type: String, default: '' },
  notice: { type: String, default: '' },
  muted: { type: Boolean, default: false },
});

const slots = useSlots();

// A faixa de destaque existe se algum detalhe pedir, ou só para carregar o
// status. Sem nenhum dos dois ela não é renderizada e o bloco começa direto
// nas linhas.
const highlightDetail = computed(() =>
  props.details.find(detail => detail.highlight)
);
const rowDetails = computed(() =>
  props.details.filter(detail => !detail.highlight)
);

// `$slots.default` e verdadeiro so por o pai ter passado conteudo, mesmo que
// todo ele esteja sob um `v-if` falso — e ai o rodape aparecia como uma faixa
// com borda e nada dentro. Por isso a checagem olha os vnodes gerados.
// Funcao, nao computed: precisa reavaliar a cada render do pai.
const isEmptyNode = node => {
  if (node.type === Comment) return true;
  if (node.type === Fragment) return (node.children || []).every(isEmptyNode);
  if (node.type === Text) return !String(node.children || '').trim();
  return false;
};

const hasActions = () => {
  const nodes = slots.default?.() || [];
  return nodes.some(node => !isEmptyNode(node));
};
</script>

<template>
  <article
    class="channel-card flex h-full flex-col rounded-xl border p-4 transition-shadow"
    :class="
      muted
        ? 'channel-card--muted border-dashed border-n-weak bg-n-solid-1'
        : 'border-n-weak bg-n-background hover:shadow-sm'
    "
  >
    <div class="channel-card__header flex items-start justify-between gap-2">
      <div class="channel-card__identity flex min-w-0 items-start gap-3">
        <!--
          text-2xl (24px): na referencia o glifo ocupa 13px de um tile de 24px
          (54%). Com text-lg dava 41% e sobrava verde demais em volta.
        -->
        <span
          class="channel-card__icon flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-2xl text-white"
          :class="[muted ? 'bg-n-slate-4 opacity-60' : iconGradient]"
        >
          <i :class="icon" class="channel-card__icon-glyph" />
        </span>
        <div class="channel-card__titles min-w-0 pt-0.5">
          <div
            class="channel-card__title-row flex flex-wrap items-center gap-1.5"
          >
            <h3
              class="channel-card__title truncate font-semibold text-n-slate-12"
            >
              {{ title }}
            </h3>
            <span
              v-for="badge in badges"
              :key="badge.label"
              class="channel-card__badge rounded-full px-2 py-0.5 text-[11px] font-semibold"
              :class="badge.class || 'bg-n-brand/15 text-n-brand'"
            >
              {{ badge.label }}
            </span>
          </div>
          <!-- Numero logo abaixo do nome, na mesma coluna ao lado do icone. -->
          <p
            v-if="subtitle"
            class="channel-card__subtitle mt-0.5 flex items-center gap-1.5 text-sm text-n-slate-11"
          >
            <i
              :class="subtitleIcon"
              class="channel-card__subtitle-icon flex-shrink-0 text-xs text-n-slate-10"
            />
            <span class="channel-card__subtitle-text truncate">
              {{ subtitle }}
            </span>
          </p>
        </div>
      </div>
      <div v-if="$slots.menu" class="channel-card__menu flex-shrink-0">
        <slot name="menu" />
      </div>
    </div>

    <div class="channel-card__body mt-3 flex-1 space-y-2 text-sm">
      <div
        v-if="details.length || statusLabel"
        class="channel-card__details space-y-2"
      >
        <!--
          Faixa do responsável: quem responde pela conexão e o estado dela, que
          são as duas coisas que se olham primeiro. O avatar redondo separa
          essa linha dos campos técnicos abaixo sem precisar de outra borda.
        -->
        <!--
          Aceleres 2026-09-04: a moldura so se desenha quando ha responsavel
          para pôr dentro dela. Sem ele sobrava uma caixa da largura toda com o
          selo de estado empurrado para o canto pelo `justify-between`, e o
          resto vazio — a mesma "faixa com borda e nada dentro" que o comentario
          do rodape, aqui em baixo, ja regista ter acontecido antes. Sem
          responsavel o selo aparece sozinho, encostado a esquerda.
        -->
        <div
          v-if="highlightDetail || statusLabel"
          class="channel-card__highlight flex items-center gap-3"
          :class="
            highlightDetail
              ? 'justify-between rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5'
              : 'justify-start'
          "
        >
          <div
            v-if="highlightDetail"
            class="channel-card__highlight-main flex min-w-0 items-center gap-2.5"
          >
            <span
              v-if="highlightDetail.icon"
              class="channel-card__highlight-avatar flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              <i :class="highlightDetail.icon" class="size-4" />
            </span>
            <div class="min-w-0 leading-tight">
              <p
                class="channel-card__highlight-label text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                {{ highlightDetail.label }}
              </p>
              <p
                class="channel-card__highlight-value truncate text-sm font-semibold"
                :class="highlightDetail.valueClass || 'text-n-slate-12'"
                :title="highlightDetail.value"
              >
                {{ highlightDetail.value }}
              </p>
            </div>
          </div>

          <span
            v-if="statusLabel"
            class="channel-card__status inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="statusClass"
          >
            <span
              class="channel-card__status-dot h-1.5 w-1.5 rounded-full bg-current"
            />
            {{ statusLabel }}
          </span>
        </div>

        <!--
          Cada campo é um tile fechado com o rótulo colado ao valor. Alinhar o
          valor na borda oposta do card, como numa tabela, jogava o dado longe
          do nome dele: com a coluna larga, os olhos tinham de atravessar o
          card para saber o que estavam lendo. Todos os tiles têm a mesma
          largura — nenhum ocupa a linha inteira — e é isso que dá a grade
          regular que faltava.
        -->
        <div
          v-if="rowDetails.length"
          class="channel-card__grid grid grid-cols-2 gap-2"
        >
          <div
            v-for="detail in rowDetails"
            :key="detail.label"
            class="channel-card__tile min-w-0 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 leading-tight"
          >
            <p
              class="channel-card__tile-label flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              <i
                v-if="detail.icon"
                :class="detail.icon"
                class="channel-card__tile-icon size-3 shrink-0"
              />
              <span class="truncate">{{ detail.label }}</span>
            </p>
            <p
              class="channel-card__tile-value mt-0.5 truncate text-sm font-semibold"
              :class="detail.valueClass || 'text-n-slate-12'"
              :title="detail.value"
            >
              {{ detail.value }}
            </p>
          </div>
        </div>
      </div>

      <p
        v-if="metaLine"
        class="channel-card__meta text-left text-xs text-n-slate-10"
      >
        {{ metaLine }}
      </p>
      <p v-if="notice" class="channel-card__notice text-xs text-n-amber-11">
        {{ notice }}
      </p>
    </div>

    <div
      v-if="hasActions()"
      class="channel-card__actions mt-3 flex flex-wrap gap-2 border-t border-n-weak pt-3"
    >
      <slot />
    </div>
  </article>
</template>
